import {
    Form,
    Row,
    Col,
    Input,
    Space,
    Button,
    message,
    Modal,
    Select,
    Card,
    Typography,
  } from "antd";
  import { useEffect, useState } from "react";
  import { useNavigate } from "react-router-dom";
  import { ExclamationCircleOutlined, LeftOutlined, SaveOutlined, DeleteOutlined, SettingOutlined, EyeOutlined } from "@ant-design/icons";
  import CustomUpload from "../customUpload";
  import axiosInstance from "../../utils/axios";
  import CustomSunEditor from "../customSunEditor";
  import SeoScore from "../SeoScore";
  
  const slugify = (text) => {
    if (!text) return "";
    return text
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();
  };

  const { Title, Text } = Typography;

  export default function BlogForm({
    id = "",
    initialValues = {},
    onSubmit = () => {},
  }) {
    const navigate = useNavigate();
    const [editorValue, setEditorValue] = useState('');
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [seoKeyword, setSeoKeyword] = useState("");
    const [formValues, setFormValues] = useState({});
    const [isEditingSlug, setIsEditingSlug] = useState(false);

    const [pagination] = useState({
      page: 1,
      pageSize: 100,
    });
  
    const fetchCategories = async () => {
      await axiosInstance.get(`/category-blog/getAll`, {params: pagination})
        .then((res) => {
          const data = res?.data;
          setCategories(data);
        })
        .catch(() => message.error("Error server!"));
    };
  
    const deleteBlog = async () => {
      await axiosInstance.delete(`/blog/delete/${id}`)
        .then((res) => message.success(String(res?.data?.message)))
    };
  
    useEffect(() => {
      fetchCategories();
      if (Object.keys(initialValues)?.length > 0) {
        form.resetFields();
        setEditorValue(initialValues?.blog_description || '');
        setFormValues(initialValues);
      }
    }, [form, initialValues]);
  
    const removeBlog = async () => {
      try {
        await deleteBlog();
        return navigate("/admin/quan-ly-blog");
      } catch (err) {
        console.log(err.message);
      }
    };
  
    const confirmDeleteBusiness = () => {
      Modal.confirm({
        icon: <ExclamationCircleOutlined className="text-red-500" />,
        content: "Bạn có chắc chắn xoá blog này?",
        okText: "Xác nhận",
        okType: 'danger',
        cancelText: "Huỷ",
        onOk: () => removeBlog(),
      });
    };

    const [debounceTimer, setDebounceTimer] = useState(null);

    const handleFormChange = (changedValues, allValues) => {
        const currentChangedValues = changedValues || {};
        const currentAllValues = allValues || form.getFieldsValue();

        // Auto-generate slug if blog_name changed and slug hasn't been manually touched
        if (currentChangedValues.blog_name && (!currentAllValues.blog_slug || !isEditingSlug)) {
            const newSlug = slugify(currentChangedValues.blog_name);
            form.setFieldsValue({ blog_slug: newSlug });
        }

        // Mark slug as manually edited if user changes it
        if (currentChangedValues.blog_slug !== undefined) {
          setIsEditingSlug(true);
        }

        if (debounceTimer) clearTimeout(debounceTimer);
        const timer = setTimeout(() => {
            setFormValues(currentAllValues);
        }, 500);
        setDebounceTimer(timer);
    };

    const generateSlugManually = () => {
      const name = form.getFieldValue("blog_name");
      if (name) {
          const newSlug = slugify(name);
          form.setFieldsValue({ blog_slug: newSlug });
          message.success("Đã tạo đường dẫn mới từ tiêu đề!");
      } else {
          message.warning("Vui lòng nhập tiêu đề bài viết trước khi tạo đường dẫn!");
      }
    };

    const onFinishFailed = (errorInfo) => {
      console.log('Failed:', errorInfo);
      const errorFields = errorInfo.errorFields.map(field => field.errors[0]).join(", ");
      message.error(`Vui lòng kiểm tra lại: ${errorFields}`);
    };

    return (
      <div className="bg-gray-50 min-h-screen p-4 md:p-8">
        <Form
          layout={"vertical"}
          form={form}
          initialValues={{ blog_status: 'public', ...initialValues }}
          onFinish={onSubmit}
          onFinishFailed={onFinishFailed}
          onValuesChange={handleFormChange}
          scrollToFirstError
        >
          {/* Header Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <Button 
                shape="circle" 
                icon={<LeftOutlined />} 
                onClick={() => navigate("/admin/quan-ly-blog")}
                className="shadow-sm"
              />
              <div>
                <Title level={4} style={{ margin: 0, fontWeight: 800 }}>
                  {id && id !== "tao-moi" ? "Chỉnh sửa bài viết" : "Tạo bài viết mới"}
                </Title>
                <Text type="secondary" className="text-xs text-gray-400">Quản lý nội dung blog của bạn</Text>
              </div>
            </div>
            
            <Space size="middle">
              {id && id !== "tao-moi" && (
                <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={confirmDeleteBusiness}
                    className="font-semibold"
                >
                    Xoá
                </Button>
              )}
              <Button 
                type="primary" 
                size="large" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                className="rounded-xl px-8 font-bold h-12 shadow-blue-200 shadow-lg"
              >
                {id && id !== "tao-moi" ? "Cập nhật bài viết" : "Lưu bài viết"}
              </Button>
            </Space>
          </div>

          <Row gutter={[24, 24]}>
            {/* Top Grid: Info & SEO */}
            <Col xs={24} lg={16}>
                <Card 
                  title={<div className="flex items-center gap-2"><SettingOutlined className="text-blue-600"/><span>Thông tin chung</span></div>}
                  className="rounded-2xl border-none shadow-sm overflow-hidden h-full"
                >
                    <Form.Item
                        label={<span className="font-bold text-gray-700">Tên bài viết (Tối ưu 50-60 ký tự)</span>}
                        name="blog_name"
                        rules={[{ required: true, message: "Tiêu đề bài viết là bắt buộc!" }]}
                    >
                        <Input 
                            size="large" 
                            placeholder="Nhập tiêu đề bài viết thu hút khách hàng..." 
                            className="rounded-lg h-12 border-gray-200"
                            maxLength={255}
                        />
                    </Form.Item>

                    <Form.Item
                      label={<span className="font-bold text-gray-700">Đường dẫn bài viết (Slug)</span>}
                      name="blog_slug"
                      rules={[{ required: true, message: "Đường dẫn bài viết là bắt buộc!" }]}
                      className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200"
                    >
                      <Input 
                          size="large" 
                          placeholder="tu-dong-theo-tieu-de" 
                          className="rounded-lg h-10 border-gray-300 font-mono text-blue-600"
                          addonBefore={window.location.origin + "/blog/"}
                          addonAfter={
                              <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<SaveOutlined className="text-blue-500" />} 
                                  onClick={generateSlugManually}
                                  className="flex items-center gap-1 hover:text-blue-700 font-bold"
                              >
                                  Tạo lại
                              </Button>
                          }
                      />
                    </Form.Item>

                    <Row gutter={24}>
                        <Col span={8}>
                            <Form.Item 
                                label={<span className="font-bold text-gray-700">Danh mục Blog</span>} 
                                name="category_blog_id"
                                rules={[{ required: true, message: "Vui lòng chọn danh mục bài viết!" }]}
                            >
                                <Select
                                  showSearch
                                  size="large"
                                  placeholder="Chọn một danh mục phù hợp"
                                  className="w-full"
                                  optionFilterProp="label"
                                  filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                  }
                                  options={categories?.data?.map((value) => ({
                                    value: value.category_blog_id,
                                    label: value.category_blog_name,
                                  }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                label={<span className="font-bold text-gray-700">Trạng thái</span>}
                                name="blog_status"
                                rules={[{ required: true, message: "Vui lòng chọn trạng thái bài viết!" }]}
                            >
                                <Select
                                    size="large"
                                    className="w-full"
                                    options={[
                                        { value: 'public', label: 'Công khai' },
                                        { value: 'draft', label: 'Bài nháp' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col span={8}>
                            <Form.Item
                                name="blog_image"
                                label={<span className="font-bold text-gray-700">Ảnh đại diện</span>}
                                rules={[{ required: true, message: "Vui lòng chọn ảnh đại diện cho bài viết!" }]}
                            >
                                <CustomUpload type="image" accept=".png, .jpg, .jpeg, .jfif" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
            </Col>

            {/* Sidebar: SEO Preview */}
            <Col xs={24} lg={8}>
                <SeoScore 
                    title={formValues.blog_name}
                    content={editorValue}
                    slug={formValues.blog_slug}
                    keyword={seoKeyword}
                    titleSeo={formValues.blog_title_seo}
                    descriptionSeo={formValues.blog_description_seo}
                />
            </Col>

            {/* Full Width SEO Config Toolbar */}
            <Col span={24}>
                <Card
                    title={<div className="flex items-center gap-2"><EyeOutlined className="text-indigo-600"/><span>Tối ưu hóa SEO Search (Google Preview)</span></div>}
                    className="rounded-2xl border-none shadow-sm overflow-hidden mb-6"
                >
                    <Row gutter={48}>
                        <Col xs={24} md={12}>
                            <Space direction="vertical" className="w-full" size="middle">
                                <Form.Item 
                                    label={<span className="font-bold text-gray-700">Thẻ tiêu đề SEO (SEO Title)</span>} 
                                    name="blog_title_seo"
                                    tooltip="Tiêu đề hiển thị trên Google. Nên từ 50-60 ký tự."
                                >
                                    <Input 
                                        placeholder="Để trống sẽ lấy tên bài viết..." 
                                        className="rounded-lg h-10 border-gray-200"
                                    />
                                </Form.Item>
                                
                                <Form.Item 
                                    label={<span className="font-bold text-gray-700">Thẻ mô tả SEO (Meta Description)</span>} 
                                    name="blog_description_seo"
                                    tooltip="Mô tả tóm tắt nội dung bài viết. Nên dưới 160 ký tự."
                                >
                                    <Input.TextArea 
                                        rows={3}
                                        placeholder="Để trống sẽ lấy đoạn đầu bài viết..." 
                                        className="rounded-lg"
                                    />
                                </Form.Item>

                                <Form.Item label={<span className="font-bold text-gray-700 text-xs">Từ khóa mục tiêu</span>}>
                                    <Input 
                                        placeholder="Ví dụ: máy đo huyết áp, sữa hạt..." 
                                        value={seoKeyword}
                                        onChange={(e) => setSeoKeyword(e.target.value)}
                                        className="rounded-lg h-10 border-gray-200"
                                    />
                                </Form.Item>
                            </Space>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="bg-gray-50 p-6 rounded-2xl border border-dashed border-gray-200 min-h-[180px]">
                                <div className="text-blue-700 font-medium mb-1 text-[13px] flex items-center gap-2">
                                    <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="google" />
                                    Google Search Preview
                                </div>
                                <div className="text-blue-800 font-bold mb-1 hover:underline text-[19px] leading-tight cursor-pointer line-clamp-1">
                                    {formValues.blog_title_seo || formValues.blog_name || "Tiêu đề bài viết hiển thị tại đây..."}
                                </div>
                                <div className="text-green-800 mb-1 text-sm flex items-center gap-1">
                                    <span className="bg-gray-100 px-2 py-0.5 rounded text-[10px] text-gray-500 font-bold uppercase">BLOG</span>
                                    {window.location.origin}/blog/{formValues.blog_slug || "duong-dan-bai-viet"}
                                </div>
                                <div className="text-gray-600 text-[14px] leading-relaxed line-clamp-2">
                                    {formValues.blog_description_seo || editorValue?.replace(/<[^>]*>/g, '')?.substring(0, 160) || "Đoạn mô tả SEO cho bài viết của bạn sẽ giúp thu hút người dùng click vào từ Google Search..."}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </Col>

            {/* FULL WIDTH EDITOR AT THE BOTTOM */}
            <Col span={24}>
                <Card 
                  title={<div className="flex items-center gap-2"><SettingOutlined className="text-blue-600"/><span>Mô tả bài viết (Editor Full Width)</span></div>}
                  className="rounded-2xl border-none shadow-sm overflow-hidden"
                >
                    <Form.Item
                        name="blog_description"
                        noStyle
                        rules={[{ required: true, message: "Vui lòng nhập nội dung!" }, { max: 1000000, message: "Nội dung quá dài!" }]}
                    >
                        <CustomSunEditor 
                            value={editorValue} 
                            onChange={(e)=> {
                                setEditorValue(e);
                                form.setFieldsValue({ blog_description: e });
                                handleFormChange();
                            }} 
                        />
                    </Form.Item>
                </Card>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }